import { biometricIdentityVerificationRequestsApi, payersApi } from "@api";
import { useSession } from "@hooks/useSession";
import { queriesPayers } from "@query";
import {
  BiometricIdentityVerificationRequest,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";

import { queriesBiometricIdentityVerificationRequests } from "../../../../v3/query/queriesBiometricIdentityVerificationRequests";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";

import { getIdentityVerificationFromCache } from "./configs/helpers/getIdentityVerificationFromCache";
import { usePayeeIdentityVerificationConfig } from "./configs/payee/usePayeeIdentityVerificationConfig";
import { usePayerIdentityVerificationConfig } from "./configs/payer/usePayerIdentityVerificationConfig";

/**
 * Hoisted so the batch read and `buildConfigFields` address the same cache
 * entry — the config resolves a missing document's pending/failed state through
 * this key.
 */
const getDataSourceQueryKey = (id: string) =>
  queriesBiometricIdentityVerificationRequests.getBiometricIdentityVerificationRequest.getQueryKey(
    {
      biometricIdentityVerificationRequestId: id
    }
  );

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  // Compared against `requirement.payerAccountId` below, so the own ACCOUNT id is
  // the right identity here. Was v1 `useUserId()` (v1 `GET /users/session`);
  // under D1/D24 the session's userId, personId and own accountId are the same
  // value, and `useSession` is reactive where the v1 query was not.
  const { accountId } = useSession();

  // A requirement's `dataSourceId` names the verification **request**, not the
  // verification: the payee-only verification reaches a payer as this request's
  // `biometricIdentityVerification` embed once the request is terminal.
  //
  // No poll predicate on purpose: the only non-terminal status is `Pending`,
  // which means "awaiting the contractor" and can last days — polling it would
  // hammer the API for no benefit.
  const identityVerificationQueries = getDataSourceQueries(
    getDataSourceQueryKey,
    requirements,
    id =>
      biometricIdentityVerificationRequestsApi.getBiometricIdentityVerificationRequest(
        {
          biometricIdentityVerificationRequestId: id
        }
      ),
    RequirementType.BIOMETRIC_IDENTITY_VERIFICATION
  );

  const payersQueries = requirements
    .filter(
      requirement =>
        requirement.type === RequirementType.BIOMETRIC_IDENTITY_VERIFICATION &&
        requirement.payerAccountId &&
        accountId !== requirement.payerAccountId
    )
    .map(requirement => ({
      queryKey: queriesPayers.getPayer.getQueryKey({
        payerId: requirement.payerAccountId ?? ""
      }),
      queryFn: () =>
        payersApi.getPayer({ payerId: requirement.payerAccountId ?? "" }),
      ...sharedRequirementsQueriesConfig
    }));

  const queries = [...identityVerificationQueries, ...payersQueries];

  return useWSQueries({ queries });
};

export const useIdentityVerificationRequirementConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.BIOMETRIC_IDENTITY_VERIFICATION] => {
  const contextQueries = useContextQueries(requirements);
  const payerConfig = usePayerIdentityVerificationConfig(callback);
  const payeeConfig = usePayeeIdentityVerificationConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  // recompute only when the batch query data updates — the payer/payee configs
  // are new objects every render and must not invalidate the memo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fields = useMemo(
    buildConfigFields<
      BiometricIdentityVerificationRequest,
      RequirementType.BIOMETRIC_IDENTITY_VERIFICATION
    >({
      payerConfig,
      payeeConfig,
      getAdditionalData: getIdentityVerificationFromCache,
      hasDataSource: true,
      getDataSourceQueryKey
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getIdentityVerificationFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    isFetching: contextQueries.some(query => query.isFetching),
    dataUpdatedAt: queriesHash
  };
};
